import { datadogRum } from '@datadog/browser-rum'
import { AppBar, Badge, Box, Divider, Menu, MenuItem, Stack, Toolbar, Tooltip } from '@mui/material'
import {
    FeatureFlagIdAccessRequest,
    FeatureFlagIdAccountProvisioning,
    FeatureFlagIdAgentConversations,
    FeatureFlagIdExportData,
    FeatureFlagIdNewNav2026,
    FeatureFlagIdOauth2Dcr,
    FeatureFlagIdOauth2PersonalClients,
    FeatureFlagIdPasswordPush,
} from 'c1gen/features'
import { ConversationsV1UiconversationsServiceCreate } from 'c1gen/permissions'
import { C1AlertTitle, C1IconButton, C1LoadingButton } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { UserAvatar } from 'components/core/avatar'
import { C1LinkButton } from 'components/core/button'
import { AgenticAiIcon } from 'components/core/icons/AgenticAiIcon'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { createURL } from 'components/core/links/docs'
import { HasCapability, useFeatureChecker } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { ConductorLogoLockup } from 'components/pages/app-bar/ConductorLogoLockup'
import { HorizontalAppBarNav } from 'components/pages/app-bar/HorizontalAppBarNav'
import { SkipNavigation } from 'components/pages/app-bar/SkipNavigation'
import { C1AppBarStyles } from 'components/pages/app-bar/styles'
import { Downloads } from 'components/pages/sidebar/Downloads'
import { getEndUserNavItems } from 'components/pages/sidebar/NavMetadata'
import { SpotlightContainer } from 'components/pages/spotlight/SpotlightContainer'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useAlert } from 'hooks/useAlert'
import { useDrawer } from 'hooks/useDrawer'
import { useWebsocketHealth } from 'hooks/useWebsocketHealth'
import { AdminModeContext, DashboardContext, UserContext } from 'lib/context/app'
import { handleLogout } from 'lib/oauth'
import { routes } from 'lib/routes'
import { useOAuth } from 'lib/rpc'
import { BookOpen, ChevronDown, Download, Plus } from 'lucide-react'
import { useRouter } from 'next/router'
import { ExportDataActionState, ListExportDataRequest } from 'pbts/c1/api/exportdata/v1/exportdata'
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { exportDataActionsListApi } from 'redux_toolkit/export-data'

const menuId = 'primary-search-account-menu'
const pageSize = 10
export const transparentStyling = {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
}

const ConnectionDegradedToastContent = (
    { forceReconnect }: { forceReconnect: () => void },
): ReactElement => {
    const [isReconnecting, setIsReconnecting] = useState(false)

    const handleForceReconnect = (): void => {
        setIsReconnecting(true)
        forceReconnect()
    }

    return <Stack direction="row" alignItems="center" spacing={3}>
        <C1AlertTitle>
            <FormattedMessage
                id="components_app_bar_connection_degraded"
                defaultMessage="Waiting for a stable connection to deliver updates"
            />
        </C1AlertTitle>
        <Box>
            <C1LoadingButton
                color="info"
                variant="outlined"
                onClick={handleForceReconnect}
                loading={isReconnecting}
                data-dd-action-name="websocket_reconnect_try_now"
            >
                <FormattedMessage id="components_app_bar_reconnect" defaultMessage="Try now" />
            </C1LoadingButton>
        </Box>
    </Stack>
}

export const C1AppBar = (): ReactElement => {
    const intl = useIntl()
    const oauth = useOAuth()
    const router = useRouter()
    const [showDrawer] = useDrawer()
    const hasApiKeysFF = useFeatureChecker(FeatureFlagIdOauth2PersonalClients)
    const hasDcrFF = useFeatureChecker(FeatureFlagIdOauth2Dcr)
    const hasAccessRequestsFF = useFeatureChecker(FeatureFlagIdAccessRequest)
    const hasAgentConversationsFF = useFeatureChecker(FeatureFlagIdAgentConversations)
    const hasExportDataFF = useFeatureChecker(FeatureFlagIdExportData)
    const hasAccountProvisioningFF = useFeatureChecker(FeatureFlagIdAccountProvisioning)
    const hasPasswordPushFF = useFeatureChecker(FeatureFlagIdPasswordPush)
    const hasNewNav = useFeatureChecker(FeatureFlagIdNewNav2026)
    const userContext = useContext(UserContext)
    const user = userContext.user
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [newDownloadsAvailable, setNewDownloadsAvailable] = useState(false)
    const dashboardContext = useContext(DashboardContext)
    const adminModeContext = useContext(AdminModeContext)
    const userNav = getEndUserNavItems(intl, dashboardContext)
    const { status, isDegraded, forceReconnect } = useWebsocketHealth()
    const toast = useAlert()
    const wasDegraded = React.useRef(false)

    useEffect(() => {
        if (isDegraded) {
            wasDegraded.current = true
            datadogRum.addError(new Error('Websocket connection degraded'), {
                timestamp: new Date().toISOString(),
            })
            toast(
                <ConnectionDegradedToastContent
                    forceReconnect={forceReconnect}
                />,
                'info',
                0,
                { vertical: 'bottom', horizontal: 'left' },
            )
        } else if (wasDegraded.current && status === 'connected') {
            wasDegraded.current = false
            datadogRum.addAction('websocket_reconnect_success', {
                timestamp: new Date().toISOString(),
            })
            toast(
                <C1AlertTitle>
                    <FormattedMessage id="components_app_bar_connection_restored" defaultMessage="Connection restored" />
                </C1AlertTitle>,
                'success',
                2000,
                { vertical: 'bottom', horizontal: 'left' },
            )
        }
    }, [isDegraded, toast, forceReconnect, status])

    const isMenuOpen = Boolean(anchorEl)

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>): void => setAnchorEl(event.currentTarget)
    const handleMenuClose = (): void => setAnchorEl(null)

    const {
        data: downloads,
        isLoading,
        isError,
        shouldInvalidateList,
    } = exportDataActionsListApi.useList(ListExportDataRequest.create({}), {
        pageSize,
        notify: true,
        actionable: !!hasExportDataFF,
        disableEssentialTableExistenceCheck: true, // DUCT-7205
    })
    const invalidateList = exportDataActionsListApi.useInvalidateList()

    useEffect(() => {
        if (isError || isLoading || !shouldInvalidateList || !downloads) {
            return
        }

        const isDataReady = downloads.flat().every((item) => (
            item.state === ExportDataActionState.READY ||
            item.state === ExportDataActionState.ERROR ||
            item.state === ExportDataActionState.PENDING
        ))
        if (isDataReady) {
            invalidateList()
            setNewDownloadsAvailable(true)
        }
    }, [shouldInvalidateList, isError, invalidateList, isLoading, downloads])

    const onLogout = (): void => {
        handleLogout(oauth)
        handleMenuClose()
    }

    const onOpenDocs = (): void => {
        window.open(createURL('/docs/product/intro') || 'https://www.conductorone.com/docs/product/intro/', '_blank')
    }

    const onOpenRequestCreate = (): void => {
        router.push('/access-requests/create')
    }

    const onOpenDownloads = (): void => {
        setNewDownloadsAvailable(false)
        showDrawer(<Downloads />)
    }

    const onOpenAgentConversations = (): void => {
        router.push('/conversations/new')
    }

    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            id={menuId}
            keepMounted
            open={isMenuOpen}
            onClose={handleMenuClose}
            sx={C1AppBarStyles.userMenuPopper}
        >
            <MenuItem component={C1Link} href={routes.profile.index} onClick={handleMenuClose} sx={{ py: 2, pl: 1 }}>
                <UserDisplayName user={user} readOnly />
            </MenuItem>
            <Divider />
            {!!hasAccountProvisioningFF &&
                <MenuItem component={C1Link} href={routes.profile.vaults} onClick={handleMenuClose} sx={C1AppBarStyles.userMenuItem}>
                    <FormattedMessage id="components_header_menu_vaults" defaultMessage="My vaults" />
                </MenuItem>
            }
            {!!hasPasswordPushFF &&
                <MenuItem component={C1Link} href={routes.passwordReset.index} onClick={handleMenuClose} sx={C1AppBarStyles.userMenuItem}>
                    <FormattedMessage id="components_header_menu_password_reset" defaultMessage="Password reset" />
                </MenuItem>
            }
            {
                (!!hasApiKeysFF || !!hasDcrFF) && <MenuItem
                    component={C1Link}
                    href={routes.profile.api}
                    onClick={handleMenuClose}
                    sx={C1AppBarStyles.userMenuItem}
                >
                    {hasDcrFF
                        ? <FormattedMessage id="components_header_menu_profile_ai_api" defaultMessage="AI & API" />
                        : <FormattedMessage id="components_header_menu_profile_api" defaultMessage="API keys" />
                    }
                </MenuItem>
            }
            <MenuItem onClick={onLogout} sx={C1AppBarStyles.userMenuItem}>
                <FormattedMessage id="components_header_menu_logout" defaultMessage="Logout" />
            </MenuItem>
        </Menu>
    )

    return <Box flexGrow={1}>
        <SkipNavigation />
        <AppBar
            id="app-bar"
            position="fixed"
            sx={sxc(
                C1AppBarStyles.appBar,
                [!adminModeContext.isOnAdminPath, { backgroundColor: 'brand.dark' }],
                [adminModeContext.isOnAdminPath, { backgroundColor: 'primary.extraDark' }],
            )}
        >
            <Toolbar>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexGrow={1}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <ConductorLogoLockup />
                        {/* Hide horizontal nav when new sidebar nav is enabled */}
                        {!hasNewNav && <HorizontalAppBarNav navItems={userNav} />}
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={2}>
                        {
                            !!hasAccessRequestsFF && <>
                                <Box sx={C1AppBarStyles.hideInSmallScreen}>
                                    <C1LinkButton
                                        startIcon={<C1Icon icon={Plus} />}
                                        href={routes.requests.create}
                                        variant="contained"
                                        color="primary"
                                        sx={{
                                            backgroundColor: router.pathname.startsWith('/access-requests/create')
                                                ? 'rgba(255, 255, 255, 0.3)'
                                                : 'rgba(255, 255, 255, 0.2  )',

                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                            },

                                            '&:focus-visible': {
                                                outline: '2px solid',
                                                outlineColor: 'primary.contrastText',
                                                outlineOffset: '2px',
                                            },
                                        }}
                                    >
                                        <FormattedMessage id="request-access" defaultMessage="Request access" />
                                    </C1LinkButton>
                                </Box>
                                <Box sx={C1AppBarStyles.showInSmallScreen}>
                                    <Tooltip
                                        PopperProps={{ sx: C1AppBarStyles.tooltipPopper }}
                                        title={intl.formatMessage({ id: 'components_app_bar_request_access', defaultMessage: 'Request access' })}
                                    >
                                        <span>
                                            <C1IconButton
                                                size="small"
                                                color="inherit"
                                                sx={transparentStyling}
                                                onClick={onOpenRequestCreate}
                                                aria-label={intl.formatMessage({ id: 'components_app_bar_request_access', defaultMessage: 'Request access' })}
                                            >
                                                <C1Icon icon={Plus} />
                                            </C1IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </>
                        }
                        {
                            !!hasAgentConversationsFF && <HasCapability permission={ConversationsV1UiconversationsServiceCreate}>
                                <Tooltip
                                    PopperProps={{ sx: C1AppBarStyles.tooltipPopper }}
                                    title={intl.formatMessage({ id: 'components_app_bar_agent_conversations', defaultMessage: 'Chat with AI' })}
                                >
                                    <span>
                                        <C1IconButton
                                            size="small"
                                            color="inherit"
                                            sx={transparentStyling}
                                            onClick={onOpenAgentConversations}
                                            aria-label={intl.formatMessage({ id: 'components_app_bar_agent_conversations', defaultMessage: 'Chat with AI' })}
                                        >
                                            <AgenticAiIcon fontSize="small" />
                                        </C1IconButton>
                                    </span>
                                </Tooltip>
                            </HasCapability>
                        }
                        <SpotlightContainer />
                        {
                            (hasExportDataFF && adminModeContext.hasAdminRole) && <Tooltip
                                PopperProps={{ sx: C1AppBarStyles.tooltipPopper }}
                                title={intl.formatMessage({ id: 'components_app_bar_download', defaultMessage: 'Downloads' })}
                            >
                                <span>
                                    <C1IconButton
                                        size="small"
                                        color="inherit"
                                        sx={transparentStyling}
                                        aria-label={intl.formatMessage({ id: 'components_app_bar_download', defaultMessage: 'Show downloads' })}
                                        onClick={onOpenDownloads}
                                    >
                                        <Badge variant="dot" color="warning" invisible={!newDownloadsAvailable}>
                                            <C1Icon icon={Download} />
                                        </Badge>
                                    </C1IconButton>
                                </span>
                            </Tooltip>
                        }
                        <Tooltip
                            PopperProps={{ sx: C1AppBarStyles.tooltipPopper }}
                            title={intl.formatMessage({ id: 'components_app_bar_docs', defaultMessage: 'Documentation' })}
                        >
                            <span>
                                <C1IconButton
                                    size="small"
                                    color="inherit"
                                    sx={transparentStyling}
                                    onClick={onOpenDocs}
                                    aria-label={intl.formatMessage({ id: 'components_app_bar_docs', defaultMessage: 'Documentation' })}
                                >
                                    <C1Icon icon={BookOpen} />
                                </C1IconButton>
                            </span>
                        </Tooltip>
                        <C1IconButton
                            size="small"
                            color="inherit"
                            onClick={handleProfileMenuOpen}
                            aria-label={intl.formatMessage({ id: 'components_header_menu_profile', defaultMessage: 'User menu' })}
                            aria-controls={menuId}
                            aria-haspopup="true"
                            aria-expanded={isMenuOpen ? 'true' : 'false'}
                            sx={{
                                ...C1AppBarStyles.userMenuAvatar,
                                borderRadius: C1BorderRadius.regular,
                                pr: 0,
                                '&:focus-visible': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.contrastText',
                                    outlineOffset: '2px',
                                },
                            }}
                        >
                            <Stack direction="row" alignItems="center" px={0} mx={0}>
                                <UserAvatar disableColorization user={user} size="md" readOnly />
                                <C1Icon icon={ChevronDown} />
                            </Stack>
                        </C1IconButton>
                    </Stack>
                </Stack>
            </Toolbar>
        </AppBar>
        {renderMenu}
    </Box>
}
